<template>
    <div>
        <div class="shared-header">
            <h3 class="shared-header__title">
                {{ $t('pages.public.shared_records.list_heading', { count: documents.length }) }}
            </h3>
            <p class="shared-header__meta">
                <span>{{ $t('pages.public.shared_records.shared_by', { name: sharedByName }) }}</span>
                <span v-if="practiceName">· {{ practiceName }}</span>
                <span v-if="expiresAt">
                    · {{ $t('pages.public.shared_records.available_until', { date: formatDate(expiresAt) }) }}
                </span>
            </p>
        </div>

        <div v-if="loadError" class="shared-records-message">
            <p>{{ loadError }}</p>
        </div>

        <div v-else class="document-list">
            <div v-for="doc in documents" :key="doc.id" class="document-card">
                <div class="doc-info">
                    <common-icon name="file" size="medium"></common-icon>
                    <div>
                        <div class="doc-title">{{ doc.fileName }}</div>
                        <div class="doc-meta">
                            <span class="doc-meta__item">
                                <common-icon name="calendar" size="tiny-small"></common-icon>
                                <span>{{ formatDate(doc.date) }}</span>
                            </span>
                            <span class="doc-meta__item">{{ formatFileSize(doc.size) }}</span>
                        </div>
                    </div>
                </div>
                <div class="doc-actions">
                    <div v-if="doc.recordType" class="doc-tag" @click="viewDocument(doc)">
                        {{ doc.recordType.name }}
                    </div>
                    <div class="doc-btns">
                        <div class="doc-btn" :title="$t('pages.public.shared_records.view')" @click="viewDocument(doc)">
                            <common-icon name="view" size="small"></common-icon>
                        </div>
                        <div class="doc-btn" :title="$t('pages.public.shared_records.download')" @click="downloadDocument(doc)">
                            <common-icon name="download" size="small"></common-icon>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <common-modal v-if="previewDoc" :heading="previewDoc.fileName" width="medium" @close="closePreview">
            <div class="preview-box">
                <div v-if="isPreviewLoading" class="loading-spinner">
                    <loading-spin></loading-spin>
                </div>
                <iframe v-else-if="isPdf && previewUrl" :src="previewUrl" class="pdf-viewer" frameborder="0"></iframe>
                <img v-else-if="isImage && previewUrl" :src="previewUrl" class="image-viewer" />
                <div v-else class="preview-placeholder">
                    <common-icon name="file" size="extra-large"></common-icon>
                    <p>{{ $t('pages.public.shared_records.preview_unavailable') }}</p>
                </div>
            </div>
            <common-buttons align="center">
                <common-button
                    :label="$t('pages.public.shared_records.download')"
                    type="primary"
                    @click="downloadDocument(previewDoc)"
                ></common-button>
            </common-buttons>
        </common-modal>
    </div>
</template>

<script>
    import DocumentSharesAPI from 'api/DocumentSharesAPI'
    import FileMixin from 'mixins/FileMixin'
    import { dateFormate } from '~/helpers'

    export default {
        mixins: [FileMixin],

        props: {
            documents: { type: Array, default: () => [] },
            token: { type: String, required: true },
            guestToken: { type: String, default: null },
            sharedByName: { type: String, default: '' },
            practiceName: { type: String, default: '' },
            expiresAt: { type: [String, Date], default: null },
            loadError: { type: String, default: '' },
        },

        data() {
            return {
                previewDoc: null,
                previewUrl: null,
                isPreviewLoading: false,
            }
        },

        computed: {
            previewExtension() {
                const name = (this.previewDoc && this.previewDoc.fileName) || ''
                return name.split('.').pop().toLowerCase()
            },

            isPdf() {
                return this.previewExtension === 'pdf'
            },

            isImage() {
                return ['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(this.previewExtension)
            },

            fileUrl() {
                return (docId) =>
                    this.guestToken
                        ? DocumentSharesAPI.getGuestDocumentFileUrl(this.token, docId)
                        : DocumentSharesAPI.getDocumentFileUrl(this.token, docId)
            },
        },

        methods: {
            formatDate(date) {
                return dateFormate(date)
            },

            formatFileSize(kb) {
                if (!kb) return '0 KB'
                if (kb < 1024) return `${kb} KB`
                return `${(kb / 1024).toFixed(1)} MB`
            },

            viewDocument(doc) {
                this.previewDoc = doc
                this.previewUrl = null
                this.isPreviewLoading = true

                this.fetchPrivateBlob(this.fileUrl(doc.id), this.guestToken)
                    .then((blob) => {
                        this.previewUrl = URL.createObjectURL(blob)
                    })
                    .catch(() => {
                        this.previewUrl = null
                    })
                    .finally(() => {
                        this.isPreviewLoading = false
                    })
            },

            closePreview() {
                if (this.previewUrl) {
                    URL.revokeObjectURL(this.previewUrl)
                }
                this.previewDoc = null
                this.previewUrl = null
            },

            downloadDocument(doc) {
                this.downloadFile(this.fileUrl(doc.id), doc.fileName, this.guestToken)
            },
        },
    }
</script>

<style scoped lang="scss">
    .shared-header {
        margin-bottom: 20px;

        &__title {
            margin-bottom: 4px;
        }

        &__meta {
            color: gray;
            font-size: 13px;
            display: flex;
            flex-wrap: wrap;
            gap: 6px;
        }
    }

    .shared-records-message {
        text-align: center;
        padding: 20px;
    }

    .document-list {
        margin-top: 8px;
    }

    .document-card {
        display: flex;
        align-items: center;
        justify-content: space-between;
        background: white;
        padding: 18px 22px;
        border-radius: 8px;
        margin-bottom: 10px;
        border: 1px solid rgba(0, 0, 0, 0.1);

        &:hover {
            background-color: #f9fafb;
        }
    }

    .doc-info {
        display: flex;
        align-items: center;
        gap: 14px;
    }

    .doc-title {
        font-weight: 500;
        font-size: 14px;
        margin-bottom: 4px;
    }

    .doc-meta {
        display: flex;
        align-items: center;
        gap: 16px;
        font-size: 12px;
        color: gray;

        &__item {
            display: flex;
            align-items: center;
            gap: 4px;
            white-space: nowrap;
        }
    }

    .doc-tag {
        background: oklch(0.95 0.0058 264.53);
        padding: 4px 8px;
        border-radius: 8px;
        font-size: 12px;
        font-weight: 500;
        color: #030213;
        cursor: pointer;
    }

    .doc-actions {
        display: flex;
        align-items: center;
        gap: 14px;
    }

    .doc-btns {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .doc-btn {
        width: 36px;
        height: 32px;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 8px;
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover {
            background-color: #e5e7eb;
        }
    }

    .preview-box {
        border: 1px solid #ddd;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 16px;
        background: #f8f9fa;
        text-align: center;
        margin-bottom: 16px;
    }

    .pdf-viewer {
        width: 100%;
        height: 600px;
        border: 0;
        border-radius: 6px;
    }

    .image-viewer {
        max-width: 100%;
        max-height: 600px;
        object-fit: contain;
        border-radius: 6px;
    }
</style>
